const DEFAULT_MAX_X_ERROR_PX = 15;
const MIN_KEYFRAME_GAP_SECONDS = 0.05;

const getInterpolatedXLeft = (startPoint, endPoint, time) => {
	const duration = endPoint.t - startPoint.t;
	if (duration <= 0) {
		return startPoint.xLeft;
	}

	const progress = (time - startPoint.t) / duration;
	return startPoint.xLeft + ((endPoint.xLeft - startPoint.xLeft) * progress);
};

const findLargestSegmentError = (points, startIndex, endIndex) => {
	let largestError = 0;
	let largestErrorIndex = startIndex;

	const startPoint = points[startIndex];
	const endPoint = points[endIndex];

	for (let pointIndex = startIndex + 1; pointIndex < endIndex; pointIndex++) {
		const point = points[pointIndex];
		const interpolatedXLeft = getInterpolatedXLeft(startPoint, endPoint, point.t);
		const error = Math.abs(point.xLeft - interpolatedXLeft);

		if (error > largestError) {
			largestError = error;
			largestErrorIndex = pointIndex;
		}
	}

	return { largestError, largestErrorIndex };
};

const simplifySegment = (points, startIndex, endIndex, tolerance, keptIndexes) => {
	if (endIndex <= startIndex + 1) {
		return;
	}

	const { largestError, largestErrorIndex } = findLargestSegmentError(points, startIndex, endIndex);

	if (largestError <= tolerance) {
		return;
	}

	keptIndexes.add(largestErrorIndex);
	simplifySegment(points, startIndex, largestErrorIndex, tolerance, keptIndexes);
	simplifySegment(points, largestErrorIndex, endIndex, tolerance, keptIndexes);
};

const normalizePoints = (points) => {
	const sortedPoints = points
		.filter(point => Number.isFinite(point.t) && Number.isFinite(point.xLeft))
		.sort((firstPoint, secondPoint) => firstPoint.t - secondPoint.t);

	if (sortedPoints.length <= 2) {
		return sortedPoints;
	}

	const firstPoint = sortedPoints[0];
	const lastPoint = sortedPoints[sortedPoints.length - 1];
	const normalizedPoints = [firstPoint];

	sortedPoints.slice(1, -1).forEach((point) => {
		const previousPoint = normalizedPoints[normalizedPoints.length - 1];
		if (Math.abs(previousPoint.t - point.t) < MIN_KEYFRAME_GAP_SECONDS) {
			if (normalizedPoints.length > 1) {
				normalizedPoints[normalizedPoints.length - 1] = point;
			}
			return;
		}

		normalizedPoints.push(point);
	});

	normalizedPoints.push(lastPoint);

	return normalizedPoints;
};

export const simplifyCropPath = (points, containerWidth, maxXErrorPx = DEFAULT_MAX_X_ERROR_PX) => {
	const normalizedPoints = normalizePoints(points);
	if (normalizedPoints.length <= 2) {
		return normalizedPoints;
	}

	const normalizedTolerance = maxXErrorPx / Math.max(containerWidth, 1);
	const keptIndexes = new Set([0, normalizedPoints.length - 1]);

	simplifySegment(normalizedPoints, 0, normalizedPoints.length - 1, normalizedTolerance, keptIndexes);

	return [...keptIndexes]
		.sort((firstIndex, secondIndex) => firstIndex - secondIndex)
		.map(index => normalizedPoints[index]);
};
